import React, { useState, useEffect } from 'react';
import { api } from './shared/utils/api';
import { formatDate } from './shared/utils/dateUtils';

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/sqetrack-import/nnlhbhccdcdadonijjgodopdkojbjdhk';

const PAGE_SIZE = 20;

// Defined outside ImportHistory to avoid DOM remount on every render
function ImportHistoryHeader() {
  return (
    <header className="imh-header">
      <div className="imh-header-inner">
        <a href="/app" className="imh-brand">
          <svg viewBox="0 0 64 64" width="28" height="28" aria-hidden="true">
            <polygon points="6,54 24,54 34,30 16,30" fill="#ffffff" opacity="0.3"/>
            <polygon points="20,46 38,46 48,22 30,22" fill="#ffffff" opacity="0.6"/>
            <polygon points="34,38 52,38 62,14 44,14" fill="#ffffff" opacity="0.95"/>
          </svg>
          <span className="imh-wordmark"><span className="imh-wm-sqe">SQE</span><span className="imh-wm-track">Track</span></span>
        </a>
      </div>
    </header>
  );
}

export default function ImportHistory({ showHeader = true }) {
  const hasToken = !!localStorage.getItem('sqetrack_token');
  const [imports,  setImports]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [shown,    setShown]    = useState(PAGE_SIZE);

  useEffect(() => {
    if (!hasToken) { setError('not-logged-in'); setLoading(false); return; }
    api('/api/import/history')
      .then(data => {
        if (data.error) throw new Error(data.error);
        setImports(data.imports || []);
      })
      .catch(e => setError(e.message || 'network'))
      .finally(() => setLoading(false));
  }, [hasToken]);

  function goToImport(id) {
    window.location.href = `/app/import-summary?id=${id}`;
  }

  if (!hasToken) return (
    <div className={showHeader ? 'imh-page' : 'page-container'}>
      {showHeader && <ImportHistoryHeader />}
      <div className="imh-content">
        <div className="imh-empty">
          <div className="imh-empty-title">Log in to view import history</div>
          <div className="imh-empty-body"><a href="/login" className="imh-login-link">Log in to SQETrack</a></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={showHeader ? 'imh-page' : 'page-container'}>
      {showHeader && <ImportHistoryHeader />}
      <div className="imh-content">
        {showHeader && <a className="imh-back" href="/app">← Back to dashboard</a>}
        <h1 className="imh-heading">Import history</h1>

        <div className="imh-card">
          {loading ? (
            <table className="imh-table">
              <thead><tr><th>Date</th><th>Source</th><th>Paper</th><th>Topics matched</th><th>Questions</th></tr></thead>
              <tbody>
                {[1,2,3].map(i => (
                  <tr key={i}>
                    {[70,60,40,40,40].map((w, j) => (
                      <td key={j}><div className="imh-shimmer" style={{width:`${w}%`}} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : error ? (
            <div className="imh-empty">
              <div className="imh-empty-title">Could not load import history</div>
              <div className="imh-empty-body">{error === 'not-logged-in' ? 'Log in to view import history.' : 'Check your connection and refresh the page.'}</div>
            </div>
          ) : imports.length === 0 ? (
            <div className="imh-empty-state">
              <svg className="imh-empty-icon" viewBox="0 0 64 64" width="52" height="52" aria-hidden="true">
                <polygon points="6,54 24,54 34,30 16,30" fill="#BFDBFE"/>
                <polygon points="20,46 38,46 48,22 30,22" fill="#60A5FA"/>
                <polygon points="34,38 52,38 62,14 44,14" fill="#2563EB"/>
              </svg>
              <h2 className="imh-empty-heading">Import your practice scores</h2>
              <p className="imh-empty-desc">
                SQETrack automatically captures your BARBRI and Law Drills results so you can see all your progress in one place.
              </p>
              <div className="imh-empty-steps">
                <div className="imh-empty-step"><span className="imh-step-num">1</span>Install the Chrome extension</div>
                <div className="imh-empty-step"><span className="imh-step-num">2</span>Visit your BARBRI or Law Drills performance page</div>
                <div className="imh-empty-step"><span className="imh-step-num">3</span>Your scores sync automatically — no manual entry needed</div>
              </div>
              <a
                className="imh-empty-cta"
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Install Chrome Extension →
              </a>
              <p className="imh-empty-footnote">Works with BARBRI and Law Drills. More providers coming soon.</p>
            </div>
          ) : (
            <>
              <table className="imh-table">
                <thead>
                  <tr>
                    <th className="imh-td-date">Date</th>
                    <th className="imh-td-source">Source</th>
                    <th className="imh-td-paper">Paper</th>
                    <th className="imh-td-topics">Study units</th>
                    <th className="imh-td-qs">Questions</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.slice(0, shown).map(imp => {
                    const isEmpty = !imp.topics_matched && !imp.questions_imported;
                    return (
                      <tr key={imp.id} onClick={() => goToImport(imp.id)} className={isEmpty ? 'imh-row-empty' : ''}>
                        <td className="imh-td-date">{formatDate(imp.imported_at)}</td>
                        <td className="imh-td-source">
                          {imp.provider === 'barbri' ? 'BARBRI' : 'Law Drills'}
                          {isEmpty && <span className="imh-empty-badge">No data</span>}
                        </td>
                        <td className="imh-td-paper">
                          {imp.paper ? (
                            <span className="imh-paper-badge">{imp.paper}</span>
                          ) : (
                            <span className="imh-paper-none">All subjects</span>
                          )}
                        </td>
                        <td className="imh-td-topics">{imp.topics_matched}</td>
                        <td className="imh-td-qs">{imp.questions_imported}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {imports.length > shown && (
                <button className="imh-load-more" onClick={() => setShown(s => s + PAGE_SIZE)}>
                  Load more ({imports.length - shown} remaining)
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
